'use client';

import { useSelectedLayoutSegments } from 'next/navigation';
import { ThemeProvider } from 'next-themes';
import type React from 'react';
import { Suspense } from 'react';
import { Toaster } from 'sonner';
import type { SerializedAuthSession } from '@/lib/auth/session-serialization';
import type { AuthSession } from '@/types/auth';
import { UniversalChatProvider } from '../chat';
import { ErrorBoundary } from '../common/ErrorBoundary';
import { QuotaBanner } from '../common/QuotaBanner';
import { SweetLinkAutoBootstrap } from '../sweetlink/SweetLinkAutoBootstrap';
import { SweetLinkResumeRedirect } from '../sweetlink/SweetLinkResumeRedirect';
import { ColorSchemeSync } from './ColorSchemeSync';
import { GlobalLinkEnhancer } from './GlobalLinkEnhancer';
import { PostHogProvider } from './PostHogProvider';
import { QueryProvider } from './QueryProvider';
import { SessionBoundary } from './SessionBoundary';

function ProvidersShell({ children }: { children: React.ReactNode }) {
  return (
    <>
      <SweetLinkAutoBootstrap />
      <SweetLinkResumeRedirect />
      {children}
    </>
  );
}

function ProvidersContent({ children }: { children: React.ReactNode }) {
  return (
    <GlobalLinkEnhancer>
      <ProvidersShell>{children}</ProvidersShell>
    </GlobalLinkEnhancer>
  );
}

function ProvidersFallback() {
  return <ProvidersShell>{null}</ProvidersShell>;
}

function RouteScopedProviders({ children }: { children: React.ReactNode }) {
  const segments = useSelectedLayoutSegments();
  const firstSegment = segments[0] ?? null;
  const isAdminRoute = firstSegment === 'admin';
  const isEmbedRoute = firstSegment === 'embed';

  if (isEmbedRoute) {
    return <ProvidersContent>{children}</ProvidersContent>;
  }

  if (isAdminRoute) {
    return <ProvidersContent>{children}</ProvidersContent>;
  }

  return (
    <Suspense fallback={<ProvidersContent>{children}</ProvidersContent>}>
      <UniversalChatProvider>
        <ErrorBoundary fallback={() => <>{children}</>}>
          <ProvidersContent>{children}</ProvidersContent>
        </ErrorBoundary>
      </UniversalChatProvider>
    </Suspense>
  );
}

export function BaseProviders({
  children,
  initialSession,
}: {
  children: React.ReactNode;
  initialSession?: AuthSession | SerializedAuthSession | null | undefined;
}) {
  return (
    <SessionBoundary initialSession={initialSession}>
      <PostHogProvider>
        <QueryProvider>
          <ThemeProvider
            attribute="class"
            defaultTheme="system"
            enableSystem
            disableTransitionOnChange={false}
            storageKey="sweetistics-theme"
          >
            <QuotaBanner />
            <ColorSchemeSync />
            <Suspense fallback={<ProvidersFallback />}>
              <RouteScopedProviders>{children}</RouteScopedProviders>
            </Suspense>
            <Toaster
              position="bottom-right"
              theme="system"
              toastOptions={{
                classNames: {
                  toast:
                    'group toast group-[.toaster]:bg-card group-[.toaster]:text-foreground group-[.toaster]:border-border/60 group-[.toaster]:shadow-lg',
                  description: 'group-[.toast]:text-muted-foreground',
                  actionButton: 'group-[.toast]:bg-primary group-[.toast]:text-primary-foreground',
                  cancelButton: 'group-[.toast]:bg-muted group-[.toast]:text-muted-foreground',
                  error:
                    'group toast group-[.toaster]:bg-destructive/10 group-[.toaster]:text-destructive group-[.toaster]:border-destructive/40',
                  success:
                    'group toast group-[.toaster]:bg-success/10 group-[.toaster]:text-success group-[.toaster]:border-success/40',
                  warning:
                    'group toast group-[.toaster]:bg-warning/10 group-[.toaster]:text-warning group-[.toaster]:border-warning/40',
                  info: 'group toast group-[.toaster]:bg-info/10 group-[.toaster]:text-info group-[.toaster]:border-info/40',
                },
              }}
            />
          </ThemeProvider>
        </QueryProvider>
      </PostHogProvider>
    </SessionBoundary>
  );
}

export function GlobalErrorProviders({ children }: { children: React.ReactNode }) {
  // Keep the global error boundary lightweight so it can render even if the main app providers fail.
  return (
    <ThemeProvider
      attribute="class"
      defaultTheme="system"
      enableSystem
      disableTransitionOnChange={false}
      storageKey="sweetistics-theme"
    >
      {children}
    </ThemeProvider>
  );
}
